/**
 * Config-key resolution for the two data-registry ids that are misnamed as
 * schema ids, during the overlap period of renaming them.
 *
 * `redactNotificationSchemaId` and `tdoRedactStateSchemaId` do NOT hold schema
 * ids. Both are passed straight to `getLatestSchemaId` as its `dataRegistryId`
 * argument, which interpolates them into `dataRegistry(id: $dataRegistryId)` —
 * they are data-registry ids, and the code already says so by renaming them on
 * destructure (`redactNotificationSchemaId: dataRegistryId`).
 *
 * The names are a fossil. Every azure config still carries a commented-out block
 * of the *original* values for `eventSchemaId`, `tdoRedactStateSchemaId` and
 * `redactNotificationSchemaId` — they were genuine schema ids once. When the app
 * moved to resolving the published schema from the registry, the values were
 * swapped for registry ids and the keys were left alone.
 *
 * Renaming a runtime-config key cannot be done in one step, because the config
 * is delivered per environment and deploys independently of the bundle. So:
 *
 *   1. (this change) The app accepts the correct `...RegistryId` name and
 *      prefers it. The legacy `...SchemaId` name still works, and using it logs
 *      one deprecation warning per key per session.
 *   2. Every environment's config adds the new key alongside the old one. Both
 *      hold the same value, so there is no flip-over moment and no cache
 *      invalidation to think about — the resolved id, and therefore the cache
 *      key, is identical either way.
 *   3. Once no environment logs the warning, the legacy key and this helper are
 *      deleted.
 *
 * Step 3 is gated on the warning, which is the point of it: it is the only
 * signal that distinguishes "every environment has been updated" from "we think
 * every environment has been updated."
 *
 * Deliberately NOT applied to `eventSchemaId`, the third key in that fossil
 * block. That one is not a mis-named id at all — `sendEvent(_schemaId, ...)`
 * ignores it and `emitAuditEvent` takes no schema argument, so it survives only
 * as a presence check gating audit emission. It is a feature flag wearing an
 * id's name, and renaming it is a different change.
 */

/** One warning per legacy key per session, not one per lookup. */
const warnedLegacyKeys = new Set<string>();

/** Test seam — the warn-once set is module state, not per-store. */
export const resetRegistryConfigWarnings = () => {
  warnedLegacyKeys.clear();
};

export interface ResolveRenamedRegistryIdArgs {
  /** Value of the correctly named `...RegistryId` config key, if present. */
  readonly registryId?: string;
  /** Value of the legacy, misnamed `...SchemaId` config key, if present. */
  readonly legacyRegistryId?: string;
  /** The correct key name, for the deprecation message. */
  readonly registryKey: string;
  /** The legacy key name, for the deprecation message. */
  readonly legacyKey: string;
}

/**
 * Prefers the correctly named key, falls back to the legacy one, and warns once
 * when the fallback is what an environment is actually running on.
 *
 * Returns `undefined` when neither is set, which callers already handle —
 * `getLatestSchemaId` dispatches `SCHEMA_ID_NOT_FOUND` and resolves nothing.
 */
export const resolveRenamedRegistryId = ({
  registryId,
  legacyRegistryId,
  registryKey,
  legacyKey,
}: ResolveRenamedRegistryIdArgs): string | undefined => {
  if (registryId) {
    return registryId;
  }
  if (legacyRegistryId) {
    if (!warnedLegacyKeys.has(legacyKey)) {
      warnedLegacyKeys.add(legacyKey);
      console.warn(
        `[redact] config key "${legacyKey}" is deprecated and misnamed — it holds a data registry id, not a schema id. Add "${registryKey}" with the same value to this environment's config. "${legacyKey}" will be removed once every environment has been updated.`
      );
    }
    return legacyRegistryId;
  }
  return undefined;
};
